import { useTranslation } from "react-i18next";
import type { ArtMetadata } from "../../lib/schedule-presets/art-metadata";
import type { StreamLayout } from "../../lib/metadata-builder/day-layouts";
import type { MetadataDay } from "../../lib/metadata-history/metadata-targets";
import { dispatchBuilderMetadata } from "../../lib/metadata-builder/edit-text";
import { endMetadataMerge } from "../../lib/metadata-history/dispatch";
import { useMetadataBuilderStore } from "../../stores/metadata-builder-store";
import { builderStreamSample } from "../../lib/metadata-builder/sample-schedule";
import { formatArtValue } from "../../lib/schedule-art/format-art-value";
import { NativeSelect } from "../ui/native-select";
import { GlobalElementPlacementControls } from "./GlobalElementPlacementControls";
import { TextStyleControls } from "./TextStyleControls";
import { MetadataField } from "../editor-panel/developer-section/MetadataField";
import { SlotSchema } from "../../lib/schedule-presets/art-metadata";
import { metadataLabels } from "../editor-panel/developer-section/metadata-form-schema";

type StreamElementEditorProps = { metadata: ArtMetadata; layout: StreamLayout; day: MetadataDay; index: number; };

export function StreamElementEditor({ metadata, layout, day, index }: StreamElementEditorProps) {
  const { t } = useTranslation();
  const samples = useMetadataBuilderStore(state => state.sampleSlots);
  const element = (layout.exceptions[day] ?? layout.shared)[index];
  if (!element) return null;
  const labels = metadataLabels(t);
  const number = element.bind.startsWith("slot2.") ? 2 : 1;
  const field = element.bind.split(".")[1];
  const icon = element.type === "icon";
  const custom = !!layout.exceptions[day];
  const sample = builderStreamSample(number, samples[number]);
  const format = metadata.formats[element.format ?? ""];
  const timeFormats = ["%H:%M", "%I:%M %p"];
  return (
    <div className="space-y-3" onBlurCapture={endMetadataMerge}>
      <h4 className="text-xs font-semibold">{labels[element.bind]}</h4>
      {field === "time" && <label className="grid gap-1 text-xs font-medium">
        {t("metadata.field.format")}
        <NativeSelect value={format ?? ""} className="h-8 py-1 text-xs" onChange={event => {
          endMetadataMerge();
          const next = structuredClone(layout);
          const target = (next.exceptions[day] ?? next.shared)[index]!;
          const name = `builder_${layout.mode}_${custom ? day : "shared"}_slot${number}_time`;
          target.format = name;
          dispatchBuilderMetadata({ type: "day-layout/set", layout: next, styles: {}, formats: { [name]: event.target.value }, action: "element", day: custom ? day : undefined });
        }}>
          {!timeFormats.includes(format ?? "") && <option value={format ?? ""} disabled>{t("builder.dates.customFormat")}</option>}
          <option value="%H:%M">{formatArtValue(sample.time || "19:00", "%H:%M")}</option>
          <option value="%I:%M %p">{formatArtValue(sample.time || "19:00", "%I:%M %p")}</option>
        </NativeSelect>
      </label>}
      <GlobalElementPlacementControls layout={layout} day={day} index={index} />
      {icon && <MetadataField schema={SlotSchema.shape.black_icon.unwrap()} value={element.black_icon ?? false}
        label={labels.black_icon!} labels={labels} onChange={value => {
          endMetadataMerge();
          const next = structuredClone(layout);
          (next.exceptions[day] ?? next.shared)[index]!.black_icon = value === true;
          dispatchBuilderMetadata({ type: "day-layout/set", layout: next, styles: {}, action: "element", day: custom ? day : undefined });
        }} />}
      {!icon && element.style && <TextStyleControls key={element.style} styleName={element.style} />}
    </div>
  );
}
